import React, { useEffect, useState, useCallback } from 'react';
import { getWellbeingEntryByDate, saveWellbeingEntry } from './lib/wellbeingService';
import { myMedicationService } from './lib/myMedicationService';
import { RangeInput } from './components/common/RangeInput';
import toast from 'react-hot-toast';
import { getErrorMessage } from './utils/errorMessages';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { EmptyState } from './components/common/EmptyState';
import { Skeleton, SkeletonStats, SkeletonList } from './components/common/Skeleton';
interface TodayProps {
  user: any;
  myMedications: any[];
  bowelMovements: any[];
  onNavigate: (section: string, params?: Record<string, string>) => void;
  onReload: () => void;
}

const Today: React.FC<TodayProps> = ({ user, myMedications, bowelMovements, onNavigate, onReload }) => {
  const [wellbeingEntry, setWellbeingEntry] = useState<any>(null);
  const [scheduleEntries, setScheduleEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuickInput, setShowQuickInput] = useState<string | false>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    if (showQuickInput) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [showQuickInput]);
  const [symptomsPeriod, setSymptomsPeriod] = useState<'week' | 'month'>('week');
  const [symptomsData, setSymptomsData] = useState<any[]>([]);
  const [symptomsLoading, setSymptomsLoading] = useState(false);

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Responsywne breakpointy
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [wellbeing, entries] = await Promise.all([
        getWellbeingEntryByDate(today),
        myMedicationService.getScheduleEntries(today, today, 'today')
      ]);
      setWellbeingEntry(wellbeing);
      setScheduleEntries(entries);
    } catch (error) {
      console.error('Błąd ładowania danych:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadData();
  }, [myMedications, loadData]);

  const loadSymptomsData = useCallback(() => {
    setSymptomsLoading(true);
    try {
      const today = new Date();
      const startDate = new Date();
      
      if (symptomsPeriod === 'week') {
        startDate.setDate(today.getDate() - 7);
      } else {
        startDate.setMonth(today.getMonth() - 1);
      }
      
      // Przygotuj dane do wykresu
      const dates: string[] = [];
      const currentDate = new Date(startDate);
      
      while (currentDate <= today) {
        dates.push(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      const chartData = dates.map(date => {
        const dayMovements = bowelMovements.filter(b => b.date === date);
        const totalCount = dayMovements.length;
        const bloodCount = dayMovements.filter(b => b.hasBlood).length;
        
        // Skrócona etykieta - tylko dzień.miesiąc
        const dayDate = new Date(date + 'T12:00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'numeric' });
        
        return {
          date: dayDate,
          fullDate: date,
          ilośćWypróżnień: totalCount > 0 ? totalCount : null,
          krew: bloodCount > 0 ? bloodCount : null
        };
      });
      
      setSymptomsData(chartData);
    } catch (error) {
      console.error('Błąd ładowania danych wypróżnień:', error);
      setSymptomsData([]);
    } finally {
      setSymptomsLoading(false);
    }
  }, [bowelMovements, symptomsPeriod]);

  useEffect(() => {
    loadSymptomsData();
  }, [loadSymptomsData]);

  const navigateToWellbeing = () => onNavigate('wellbeing', { date: today });

  const handleUpdateWellbeing = async (updates: Record<string, any>) => {
    try {
      const base = wellbeingEntry ? { ...wellbeingEntry } : { date: today };
      const updated = await saveWellbeingEntry({ ...base, ...updates });
      setWellbeingEntry(updated);
    } catch (error) {
      console.error('Błąd zapisywania:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const todayBowelMovements = bowelMovements.filter(b => b.date === today);
  const bloodCount = todayBowelMovements.filter(b => b.hasBlood).length;
  const takenMedsCount = scheduleEntries.filter(e => e.taken === true).length;
  const totalMedsCount = scheduleEntries.length;
  const daysOfData = new Set(bowelMovements.map(b => b.date)).size;

  // Responsywne wartości - funkcje pomocnicze
  const rem = (px: number) => `${px / 16}rem`;
  const clamp = (min: number, preferred: number, max: number) => 
    `clamp(${rem(min)}, ${preferred}vw, ${rem(max)})`;
  
  // Responsywne wartości - zmienne (kompaktowe na mobile)
  const contentPadding = isMobile ? rem(12) : isTablet ? rem(16) : clamp(16, 1.5, 32);
  const gap = isMobile ? rem(6) : isTablet ? rem(16) : clamp(16, 1.5, 24);
  const cardPadding = isMobile ? rem(10) : isTablet ? rem(20) : clamp(20, 1.5, 28);
  const borderRadius = isMobile ? rem(16) : rem(24);
  const fontSize = {
    xs: rem(10),
    sm: rem(12),
    base: rem(14),
    lg: rem(16),
    xl: rem(18),
    '2xl': rem(20),
    '3xl': clamp(24, 2.5, 32),
    '4xl': clamp(32, 3.5, 48)
  };

  if (loading) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        margin: '0',
        background: '#FAFAFA',
        width: '100%',
      }}>
        <div className="max-w-4xl mx-auto w-full" style={{
          flex: 1,
          overflow: 'auto',
          padding: contentPadding,
          display: 'flex',
          flexDirection: 'column',
          gap: gap,
          minHeight: 0,
        }}>
          <SkeletonStats />
          <SkeletonList count={3} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
      margin: '0',
      background: '#FAFAFA',
      width: '100%',
      paddingBottom: '30px'
    }}>
      {/* Main content - wycentrowany na desktop */}
      <div className="max-w-4xl mx-auto w-full" style={{ 
        flex: 1,
        overflow: 'auto',
        padding: contentPadding,
        display: 'flex',
        flexDirection: 'column',
        gap: gap,
        minHeight: 0
      }}>
        {/* Quick Actions - ikony jak w nav (Book, Heart, Pill) */}
        <div style={{
          marginBottom: gap,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: rem(12),
        }}>
          {/* PRIMARY: Dodaj wypróżnienie */}
          <button
            onClick={() => onNavigate('bowel')}
            style={{
              background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
              border: 'none',
              borderRadius: rem(16),
              padding: `${rem(20)} ${rem(24)}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: rem(16),
              boxShadow: '0 8px 24px rgba(107, 127, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: rem(80),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(107, 127, 255, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(107, 127, 255, 0.25)';
            }}
          >
            <div style={{
              width: rem(48),
              height: rem(48),
              borderRadius: rem(14),
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left', color: 'white' }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: '600', marginBottom: rem(4) }}>Dodaj wypróżnienie</div>
              <div style={{ fontSize: fontSize.base, opacity: 0.9 }}>Rejestruj objawy na bieżąco</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          {/* SECONDARY: Samopoczucie */}
          <button
            onClick={() => onNavigate('wellbeing')}
            style={{
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: rem(16),
              padding: `${rem(20)} ${rem(24)}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: rem(16),
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: rem(80),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{
              width: rem(48),
              height: rem(48),
              borderRadius: rem(14),
              background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: '600', color: '#0f172a', marginBottom: rem(4) }}>Jak się czujesz?</div>
              <div style={{ fontSize: fontSize.base, color: '#64748b' }}>Dzienny check-in samopoczucia</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          {/* SECONDARY: Dzisiejsze leki */}
          <button
            onClick={() => onNavigate('my-medications-today')}
            style={{
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: rem(16),
              padding: `${rem(20)} ${rem(24)}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: rem(16),
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: rem(80),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{
              width: rem(48),
              height: rem(48),
              borderRadius: rem(14),
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.5 20.5L3.5 13.5a5 5 0 117.07-7.07l9.9 9.9a5 5 0 11-7.07 7.07L10.5 20.5z"/>
                <path d="M8.5 8.5l7 7"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: '600', color: '#0f172a', marginBottom: rem(4) }}>Dzisiejsze leki</div>
              <div style={{ fontSize: fontSize.base, color: '#64748b' }}>Odhacz przyjęte</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        {/* Welcome banner - nowy użytkownik (< 3 dni danych) */}
        {daysOfData < 3 && (
          <div style={{ marginBottom: gap }}>
            <EmptyState
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              }
              title="Witaj w IBD Tracker!"
              description="Zacznij rejestrować swoje objawy regularnie, aby zobaczyć wzorce i trendy w swoim zdrowiu."
              actionLabel="Dodaj pierwsze wypróżnienie"
              onAction={() => onNavigate('bowel')}
            />
          </div>
        )}
        
        {/* Rząd 1: Liczba wypróżnień | Przyjęte leki | Objawy (z filtrem) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: gap,
          flexShrink: 0,
          width: '100%'
        }}>

          {/* Objawy - wykres z filtrem */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: borderRadius,
            padding: cardPadding,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #E5E5E5',
            minHeight: isMobile ? rem(90) : clamp(100, 10, 160),
            gridColumn: 'span 3'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: rem(8)
            }}>
              <div style={{ fontSize: fontSize.sm, color: '#999999' }}>
                Objawy
              </div>
              <div style={{ display: 'flex', gap: rem(4) }}>
                <button
                  onClick={() => setSymptomsPeriod('week')}
                  style={{
                    padding: `${rem(4)} ${rem(12)}`,
                    background: symptomsPeriod === 'week' ? 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)' : 'transparent',
                    color: symptomsPeriod === 'week' ? '#FFFFFF' : '#999999',
                    border: '1px solid #E5E5E5',
                    borderRadius: rem(8),
                    fontSize: fontSize.xs,
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)'
                  }}
                >
                  TYDZIEŃ
                </button>
                <button
                  onClick={() => setSymptomsPeriod('month')}
                  style={{
                    padding: `${rem(4)} ${rem(12)}`,
                    background: symptomsPeriod === 'month' ? 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)' : 'transparent',
                    color: symptomsPeriod === 'month' ? '#FFFFFF' : '#999999',
                    border: '1px solid #E5E5E5',
                    borderRadius: rem(8),
                    fontSize: fontSize.xs,
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)'
                  }}
                >
                  MIESIĄC
                </button>
              </div>
            </div>
            <div style={{
              width: '100%',
              height: isMobile ? 'auto' : 280,
              minHeight: isMobile ? 180 : undefined,
              background: '#F5F5F5',
              borderRadius: rem(12),
              padding: rem(12),
              display: 'flex',
              flexDirection: 'column'
            }}>
              {symptomsLoading ? (
                <div style={{ padding: rem(16), height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Skeleton width="100%" height="120px" borderRadius={borderRadius} />
                </div>
              ) : symptomsData.length === 0 ? (
                <div style={{ color: '#999999', fontSize: fontSize.sm, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  Brak danych do wyświetlenia
                </div>
              ) : isMobile ? (
                /* Mobile: prosta wizualizacja słupkowa - bez Recharts, zawsze renderuje */
                <div className="space-y-3">
                  {symptomsData.map((day, idx) => {
                    const count = day.ilośćWypróżnień ?? 0;
                    const blood = day.krew ?? 0;
                    const total = count + blood;
                    const maxVal = Math.max(1, ...symptomsData.map(d => (d.ilośćWypróżnień ?? 0) + (d.krew ?? 0)));
                    const barPct = maxVal > 0 ? (total / maxVal) * 100 : 0;
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-11 flex-shrink-0">{day.date}</span>
                        <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden flex min-w-0">
                          {total > 0 ? (
                            <div className="h-full flex" style={{ width: `${barPct}%`, minWidth: 40 }}>
                              {count > 0 && (
                                <div 
                                  className="h-full flex items-center justify-center bg-[#6B7FFF]"
                                  style={{ width: `${(count / total) * 100}%`, minWidth: 24 }}
                                >
                                  <span className="text-xs font-bold text-white">{count}</span>
                                </div>
                              )}
                              {blood > 0 && (
                                <div 
                                  className="h-full flex items-center justify-center bg-[#FF4444]"
                                  style={{ width: `${(blood / total) * 100}%`, minWidth: 20 }}
                                >
                                  <span className="text-xs font-bold text-white">{blood}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 pl-2 self-center">0</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-600 w-5 text-right flex-shrink-0">{total}</span>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                    <span><span className="inline-block w-3 h-3 rounded bg-[#6B7FFF] mr-1" />Ilość wypróżnień</span>
                    <span><span className="inline-block w-3 h-3 rounded bg-[#FF4444] mr-1" />Krew</span>
                  </div>
                </div>
              ) : (
                /* Desktop: Recharts LineChart */
                <div style={{ width: '100%', height: 250, minHeight: 200 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={symptomsData} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#999999"
                        tick={{ fontSize: 10 }}
                        interval={symptomsPeriod === 'week' ? 0 : 'preserveStartEnd'}
                        angle={-45}
                        textAnchor="end"
                        height={35}
                      />
                      <YAxis 
                        stroke="#999999"
                        tick={{ fontSize: 10 }}
                        domain={[0, 'dataMax']}
                        allowDecimals={false}
                        width={30}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E5E5',
                          borderRadius: rem(8),
                          fontSize: fontSize.xs
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: fontSize.xs, paddingTop: rem(8) }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ilośćWypróżnień" 
                        stroke="#6B7FFF" 
                        strokeWidth={2}
                        dot={{ fill: '#6B7FFF', r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Ilość wypróżnień"
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="krew" 
                        stroke="#FF4444" 
                        strokeWidth={2}
                        dot={{ fill: '#FF4444', r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Krew"
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Liczba wypróżnień */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: borderRadius,
            padding: cardPadding,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            border: '1px solid #E5E5E5',
            minHeight: clamp(100, 10, 160)
          }}>
            <div style={{ fontSize: fontSize.sm, color: '#999999', marginBottom: rem(6) }}>
              Liczba wypróżnień
            </div>
            <div style={{ fontSize: isMobile ? fontSize['3xl'] : fontSize['4xl'], fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {todayBowelMovements.length}
            </div>
          </div>

          {/* Krew */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: borderRadius,
            padding: cardPadding,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            minHeight: clamp(100, 10, 160),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            border: '1px solid #E5E5E5'
          }}>
            <div style={{ fontSize: fontSize.sm, color: '#999999' }}>Krew</div>
            <div style={{ 
              fontSize: fontSize['4xl'], 
              fontWeight: 'var(--font-weight-medium)', 
              color: bloodCount > 0 ? '#FF4444' : '#000',
              borderRadius: rem(12),
              padding: rem(8),
              display: 'inline-block',
              width: 'fit-content'
            }}>
              {bloodCount}{todayBowelMovements.length > 0 && <span style={{ fontSize: fontSize['2xl'], color: '#999999', fontWeight: 'normal' }}>/{todayBowelMovements.length}</span>}
            </div>
            <div style={{ display: 'flex', gap: rem(4) }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: rem(4),
                  background: bloodCount > i ? '#6B8CFF' : '#F5F5F5',
                  borderRadius: rem(2)
                }} />
              ))}
            </div>
          </div>

          {/* Przyjęte leki */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: borderRadius,
            padding: cardPadding,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            border: '1px solid #E5E5E5',
            minHeight: clamp(100, 10, 160)
          }}>
            <div style={{ fontSize: fontSize.sm, color: '#999999', marginBottom: rem(6) }}>
              Przyjęte leki
            </div>
            <div style={{ fontSize: isMobile ? fontSize['3xl'] : fontSize['4xl'], fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {takenMedsCount}{totalMedsCount > 0 && <span style={{ fontSize: fontSize['2xl'], color: '#999999', fontWeight: 'normal' }}>/{totalMedsCount}</span>}
            </div>
          </div>
        </div>

        {/* Rząd 2: Sen | Energia | Stres */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: gap,
          flexShrink: 0,
          width: '100%'
        }}>
          {/* Sen */}
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: borderRadius,
              padding: cardPadding,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              minHeight: clamp(100, 10, 160),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              border: '1px solid #E5E5E5'
            }}
            onClick={() => setShowQuickInput('sleep')}
          >
            <div style={{ fontSize: fontSize.sm, color: '#999999' }}>Sen</div>
            <div style={{ fontSize: fontSize['4xl'], fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {wellbeingEntry?.sleepHours ?? 0}
            </div>
            <div style={{ display: 'flex', gap: rem(4) }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: rem(4),
                  background: (wellbeingEntry?.sleepHours ?? 0) > i * 2 ? '#6B7FFF' : '#F5F5F5',
                  borderRadius: rem(2)
                }} />
              ))}
            </div>
          </div>

          {/* Energia */}
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: borderRadius,
              padding: cardPadding,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              minHeight: clamp(100, 10, 160),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              border: '1px solid #E5E5E5'
            }}
            onClick={() => setShowQuickInput('energy')}
          >
            <div style={{ fontSize: fontSize.sm, color: '#999999' }}>Energia</div>
            <div style={{ fontSize: fontSize['4xl'], fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {wellbeingEntry?.energy ?? 0}
            </div>
            <div style={{ display: 'flex', gap: rem(4) }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: rem(4),
                  background: (wellbeingEntry?.energy ?? 0) > i ? '#6B7FFF' : '#F5F5F5',
                  borderRadius: rem(2)
                }} />
              ))}
            </div>
          </div>

          {/* Stres */}
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: borderRadius,
              padding: cardPadding,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              minHeight: clamp(100, 10, 160),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              border: '1px solid #E5E5E5'
            }}
            onClick={() => setShowQuickInput('stress')}
          >
            <div style={{ fontSize: fontSize.sm, color: '#999999' }}>Stres</div>
            <div style={{ fontSize: fontSize['4xl'], fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {wellbeingEntry?.stress ?? 0}
            </div>
            <div style={{ display: 'flex', gap: rem(4) }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: rem(4),
                  background: (wellbeingEntry?.stress ?? 0) > i ? '#6B7FFF' : '#F5F5F5',
                  borderRadius: rem(2)
                }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal szybkiego wprowadzania – zapis do wellbeing (spójne z Samopoczucie) */}
      {showQuickInput && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: rem(20)
          }}
          onClick={() => setShowQuickInput(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: isMobile ? rem(16) : borderRadius,
              padding: isMobile ? rem(16) : cardPadding,
              maxWidth: clamp(280, 90, 400),
              width: '100%',
              color: '#1A1A1A',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: rem(20), fontSize: fontSize.xl, fontWeight: 'var(--font-weight-medium)', color: '#1A1A1A' }}>
              {showQuickInput === 'energy' && 'Energia/Zmęczenie'}
              {showQuickInput === 'stress' && 'Poziom stresu'}
              {showQuickInput === 'sleep' && 'Sen (godziny)'}
            </h3>
            
            {showQuickInput === 'energy' && (
              <RangeInput
                label="Energia/Zmęczenie"
                value={wellbeingEntry?.energy ?? 0}
                min={0}
                max={5}
                onChange={(value) => handleUpdateWellbeing({ energy: value })}
                unit="/5"
              />
            )}
            
            {showQuickInput === 'stress' && (
              <RangeInput
                label="Poziom stresu"
                value={wellbeingEntry?.stress ?? 0}
                min={0}
                max={5}
                onChange={(value) => handleUpdateWellbeing({ stress: value })}
                unit="/5"
              />
            )}
            
            {showQuickInput === 'sleep' && (
              <RangeInput
                label="Sen (godziny)"
                value={wellbeingEntry?.sleepHours ?? 0}
                min={0}
                max={12}
                onChange={(value) => handleUpdateWellbeing({ sleepHours: value })}
                unit="h"
              />
            )}
            
            <div style={{ display: 'flex', gap: rem(8), marginTop: rem(20) }}>
              <button
                onClick={navigateToWellbeing}
                style={{
                  flex: 1,
                  padding: rem(14),
                  background: 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: fontSize.base
                }}
              >
                Pełny formularz
              </button>
              <button
                onClick={() => setShowQuickInput(false)}
                style={{
                  flex: 1,
                  padding: rem(14),
                  background: '#F5F5F5',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: fontSize.base,
                  color: '#1A1A1A'
                }}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Today;
